import { forwardRef, useId, type ChangeEvent, type ReactNode } from 'react';
import { classNames } from '~/utils/classNames';

export type InputVariant = 'vertical' | 'horizontal';
export type InputSize = 'md' | 'sm';

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: ReactNode;
  placeholder?: string;
  variant?: InputVariant;
  size?: InputSize;
  error?: ReactNode;
  warning?: boolean;
  hint?: ReactNode;
  inputClassName?: string;
  disabled?: boolean;
  onValueChange?: (value: string) => void;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      variant = 'vertical',
      size = 'md',
      error,
      warning,
      hint,
      className,
      disabled = false,
      placeholder,
      inputClassName,
      onChange,
      onValueChange,
      ...rest
    },
    ref,
  ) => {
    const id = useId();

    const inputId = `${id}-input`;
    const hintId = `${id}-hint`;

    function handleOnChange(event: ChangeEvent<HTMLInputElement>) {
      onValueChange?.(event.target.value);

      onChange?.(event);
    }

    return (
      <div
        className={classNames(
          'flex',
          {
            'flex-col gap-1.5': variant === 'vertical',
            'items-center justify-between gap-15': variant === 'horizontal',
            'op-75': disabled,
          },
          className,
        )}
      >
        {label && (
          <label
            htmlFor={inputId}
            className={classNames('text-bolt-ds-textSecondary font-medium', {
              'text-sm': size === 'md',
              'text-xs': size === 'sm',
            })}
          >
            {label}
          </label>
        )}

        <div className="flex grow-1 flex-col gap-1.5">
          <input
            ref={ref}
            {...rest}
            className={classNames(
              'flex items-center',
              'px-3 py-2.5 bg-bolt-ds-surfaceOne border rounded-md focus:ring-2 placeholder:text-bolt-ds-textTertiary outline-none',
              'disabled:cursor-not-allowed disabled:text-bolt-ds-textTertiary',
              'truncate',
              {
                'h-11 text-base': size === 'md',
                'h-10 text-sm': size === 'sm',
                'border-bolt-ds-borderOutline focus:ring-bolt-ds-brandHighlight': !error,
                'border-bolt-ds-danger focus:ring-bolt-ds-dangerHighlight': !!error && !warning,
                'border-bolt-ds-warning focus:ring-bolt-ds-warningHighlight': !!error && warning,
                'grow-1': variant === 'horizontal',
              },
              inputClassName,
            )}
            placeholder={placeholder}
            disabled={disabled}
            id={inputId}
            aria-describedby={hintId}
            onChange={handleOnChange}
          />

          {variant === 'vertical' && (hint || error) && (
            <p
              className={classNames({
                'text-sm': size === 'md',
                'text-xs': size === 'sm',
                'text-bolt-ds-textTertiary': !error,
                'text-bolt-ds-danger': !!error && !warning,
                'text-bolt-ds-warning': !!error && warning,
              })}
              id={hintId}
            >
              {error || hint}
            </p>
          )}
        </div>
      </div>
    );
  },
);
